import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import MarketingLayout from '../components/MarketingLayout';
import { blogArtFor } from '../lib/blogArt';
import {
  SITE_URL,
  blogAlternates,
  blogIndexPath,
  blogLangFromPath,
  blogPostPath,
  otherBlogLang,
} from '../lib/blogPaths';

/* ── Load all post JSON files from content/posts at build time ── */
const postModules = require.context('../content/posts', false, /\.json$/);
const ALL_POSTS = postModules.keys().map((key) => {
  const post = postModules(key);
  return { ...post, _file: key };
});

const formatDate = (iso, lang) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return null;
  }
};

const Blog = () => {
  // The URL decides the language, not the i18n session state: /blog is the
  // English edition and /it/blog the Italian one, so each can be indexed
  // separately. Switching the app's language no longer rewrites this page in
  // place — the switcher below navigates to the other URL instead.
  const { pathname } = useLocation();
  const lang = blogLangFromPath(pathname);

  const posts = useMemo(() => {
    return ALL_POSTS
      .filter((p) => p[lang]?.title)
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }, [lang]);

  const [featured, ...rest] = posts;

  // Distinct per language: the two index pages are hreflang alternates, and
  // an identical title on both wastes the strongest signal each one has.
  const title =
    lang === 'it'
      ? 'Idee da portare a casa — Better Husband Blog'
      : 'Ideas worth bringing home — Better Husband Blog';
  const description =
    lang === 'it'
      ? 'Consigli pratici per essere un marito e padre migliore, ogni giorno.'
      : 'Practical tips to become a better husband and father, every day.';

  return (
    <MarketingLayout>
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={SITE_URL + blogIndexPath(lang)} />
        <link rel="canonical" href={SITE_URL + blogIndexPath(lang)} />
        {blogAlternates().map((alt) => (
          <link key={alt.hrefLang} rel="alternate" hrefLang={alt.hrefLang} href={alt.href} />
        ))}
      </Helmet>

      <main className="bh-container" style={{ paddingTop: 64, paddingBottom: 96, flex: 1 }}>
        <header style={{ maxWidth: 640, marginBottom: 56 }}>
          <span className="bh-eyebrow">{lang === 'it' ? 'Il blog' : 'The blog'}</span>
          <h1 style={{ fontSize: 'clamp(2.4rem, 4.5vw, 3.4rem)', marginBottom: 16 }}>
            {lang === 'it' ? 'Idee da portare a casa.' : 'Ideas worth bringing home.'}
          </h1>
          <p className="bh-lead">{description}</p>
          <p style={{ marginTop: 12, fontSize: 14 }}>
            <Link to={blogIndexPath(otherBlogLang(lang))} hrefLang={otherBlogLang(lang)}>
              {lang === 'it' ? 'Read in English' : 'Leggi in italiano'}
            </Link>
          </p>
        </header>

        {posts.length === 0 ? (
          <p style={{ color: 'var(--muted)' }}>
            {lang === 'it' ? 'Nessun articolo ancora.' : 'No articles yet.'}
          </p>
        ) : (
          <>
            {/* Featured latest post */}
            <Link
              to={blogPostPath(lang, featured.slug)}
              className="bh-card"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                overflow: 'hidden',
                textDecoration: 'none',
                marginBottom: 40,
              }}
            >
              <img
                src={blogArtFor(featured).url}
                alt={blogArtFor(featured).alt || featured[lang].title}
                style={{ width: '100%', height: '100%', minHeight: 260, objectFit: 'cover' }}
              />
              <div style={{ padding: 40 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                  {(featured.tags || []).slice(0, 3).map((tag) => (
                    <span key={tag} className="bh-tag">{tag}</span>
                  ))}
                </div>
                <h2 style={{ fontSize: '1.9rem', marginBottom: 12 }}>{featured[lang].title}</h2>
                <p style={{ color: 'var(--muted)', marginBottom: 16 }}>
                  {featured[lang].subtitle || featured[lang].meta_description}
                </p>
                <span style={{ fontSize: 14, color: 'var(--muted)' }}>
                  {formatDate(featured.created_at, lang)}
                  {featured.reading_time_min &&
                    ` · ${featured.reading_time_min} min ${lang === 'it' ? 'di lettura' : 'read'}`}
                </span>
              </div>
            </Link>

            {/* Post grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
              {rest.map((post) => {
                const localized = post[lang];
                return (
                  <Link
                    key={post.slug}
                    to={blogPostPath(lang, post.slug)}
                    className="bh-card"
                    style={{ overflow: 'hidden', textDecoration: 'none', display: 'flex', flexDirection: 'column' }}
                  >
                    <img
                      src={blogArtFor(post).url}
                      alt={blogArtFor(post).alt || localized.title}
                      style={{ width: '100%', height: 176, objectFit: 'cover' }}
                      loading="lazy"
                    />
                    <div style={{ padding: 28, display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                        {(post.tags || []).slice(0, 2).map((tag) => (
                          <span key={tag} className="bh-tag">{tag}</span>
                        ))}
                      </div>
                      <h2 style={{ fontSize: '1.25rem', marginBottom: 8 }}>{localized.title}</h2>
                      <p style={{ fontSize: 15, color: 'var(--muted)', flex: 1 }} className="line-clamp-2">
                        {localized.subtitle || localized.meta_description}
                      </p>
                      <span style={{ fontSize: 13, color: 'var(--muted)', marginTop: 16 }}>
                        {formatDate(post.created_at, lang)}
                        {post.reading_time_min &&
                          ` · ${post.reading_time_min} min ${lang === 'it' ? 'di lettura' : 'read'}`}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </main>
    </MarketingLayout>
  );
};

export default Blog;
